import axiosInstance from '../helpers/axios'
import { officeConstants, officeSalesSessionConstants } from './constants'
import swal from 'sweetalert'


const SESSION_CART_UPDATE_REQUEST = 'OFFICE_SALES_SESSION_CART_UPDATE_REQUEST'
const SESSION_CART_UPDATE_SUCCESS = 'OFFICE_SALES_SESSION_CART_UPDATE_SUCCESS'
const SESSION_CART_UPDATE_FAILURE = 'OFFICE_SALES_SESSION_CART_UPDATE_FAILURE'
const SESSION_CONVERT_ORDER_REQUEST = 'OFFICE_SALES_SESSION_CONVERT_ORDER_REQUEST'
const SESSION_CONVERT_ORDER_SUCCESS = 'OFFICE_SALES_SESSION_CONVERT_ORDER_SUCCESS'
const SESSION_CONVERT_ORDER_FAILURE = 'OFFICE_SALES_SESSION_CONVERT_ORDER_FAILURE'

const getError = (error, fallback = 'Ошибка работы с сессиями оформления') => {
    return error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
}


const DEBUG_SCENE_SAVE_TIMING = true

const getSceneSaveNow = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
)

const getJsonByteLength = (value) => {
    try {
        const json = JSON.stringify(value ?? null)
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length
        return json.length
    } catch (_error) {
        return 0
    }
}

const logSceneSaveRequest = (...args) => {
    if (!DEBUG_SCENE_SAVE_TIMING || typeof console === 'undefined') return
    console.log('[SCENE-SAVE][http]', ...args)
}

const createBillingRequestId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }

    return `scene-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const buildBillingSummaryFromCharge = (charge = null) => {
    if (!charge || typeof charge !== 'object') return null

    const summary = {}
    const includedTotal = charge.includedTotal
    const includedUsed = charge.includedUsed

    if (charge.balanceAfter !== undefined && charge.balanceAfter !== null) {
        summary.balance = Number(charge.balanceAfter || 0)
    }

    if (includedTotal !== undefined && includedTotal !== null) {
        const total = Math.max(0, Number(includedTotal || 0))
        const used = Math.max(0, Number(includedUsed || 0))
        summary.includedScenes = {
            total,
            used,
            remaining: Math.max(0, total - used),
        }
    }

    if (charge.packageCreditsRemaining !== undefined && charge.packageCreditsRemaining !== null) {
        summary.packageCredits = Math.max(0, Number(charge.packageCreditsRemaining || 0))
    }

    return Object.keys(summary).length ? summary : null
}

const applyOfficeBillingSummary = (dispatch, officeId, summary) => {
    const cleanOfficeId = String(officeId || '').trim()
    if (!cleanOfficeId || !summary || typeof summary !== 'object') return

    dispatch({
        type: officeConstants.PATCH_OFFICE_BILLING_SUMMARY,
        payload: {
            officeId: cleanOfficeId,
            summary,
        },
    })
}

const refreshOfficeBillingSummary = async (dispatch, officeId) => {
    const cleanOfficeId = String(officeId || '').trim()
    if (!cleanOfficeId || cleanOfficeId === 'all') return

    try {
        const response = await axiosInstance.get(`/billing/office/${cleanOfficeId}/summary`)
        const summary = response.data?.summary || null
        if (summary) applyOfficeBillingSummary(dispatch, cleanOfficeId, summary)
    } catch (_error) {
        // Локальное состояние уже обновлено из ответа операции.
        // Фоновая сверка не должна ломать успешное создание сцены.
    }
}

const syncOfficeBillingFromResponse = (dispatch, officeId, responseData = {}) => {
    const summary = responseData?.billing?.summary ||
        buildBillingSummaryFromCharge(responseData?.session?.billingCharge)

    if (!summary) return

    applyOfficeBillingSummary(dispatch, officeId, summary)
    void refreshOfficeBillingSummary(dispatch, officeId)
}

const officeSalesSessionReadRequests = new Map()
const officeSalesSessionListRequests = new Map()

const buildOfficeSalesSessionListRequestKey = (officeId, params = {}) => {
    const cleanOfficeId = String(officeId || '').trim()
    if (!cleanOfficeId) return ''

    const requestParams = Object.entries(params || {})
        .filter(([key, value]) => (
            !['store', 'append'].includes(key) &&
            value !== undefined &&
            value !== null &&
            value !== ''
        ))
        .sort(([a], [b]) => a.localeCompare(b))

    return JSON.stringify([cleanOfficeId, requestParams])
}

const getOfficeSalesSessionListResponse = (officeId, params = {}) => {
    const requestParams = Object.entries(params || {}).reduce((result, [key, value]) => {
        if (['store', 'append'].includes(key)) return result
        if (value === undefined || value === null || value === '') return result
        result[key] = value
        return result
    }, {})
    requestParams.view = 'summary'

    const requestKey = buildOfficeSalesSessionListRequestKey(officeId, requestParams)
    const existing = requestKey ? officeSalesSessionListRequests.get(requestKey) : null

    if (existing) return existing

    const request = axiosInstance.get(`/office-sales-session/office/${officeId}`, {
        params: requestParams,
    }).finally(() => {
        if (requestKey && officeSalesSessionListRequests.get(requestKey) === request) {
            officeSalesSessionListRequests.delete(requestKey)
        }
    })

    if (requestKey) officeSalesSessionListRequests.set(requestKey, request)

    return request
}

const getOfficeSalesSessionReadKey = (officeId, sessionId) => {
    const cleanOfficeId = String(officeId || '').trim()
    const cleanSessionId = String(sessionId || '').trim().toUpperCase()

    if (!cleanOfficeId || !cleanSessionId) return ''

    return `${cleanOfficeId}:${cleanSessionId}`
}

export const listOfficeSalesSessions = (officeId, params = {}) => {
    return async (dispatch) => {
        try {
            const storeResult = params.store !== false
            if (storeResult) dispatch({
                type: officeSalesSessionConstants.LIST_OFFICE_SALES_SESSIONS_REQUEST,
                payload: {
                    officeId: String(officeId || ''),
                },
            })

            const response = await getOfficeSalesSessionListResponse(officeId, params)

            if (storeResult) dispatch({
                type: officeSalesSessionConstants.LIST_OFFICE_SALES_SESSIONS_SUCCESS,
                payload: {
                    officeId: String(officeId || ''),
                    sessions: response.data.sessions || [],
                    pagination: response.data.pagination || null,
                    append: params.append === true,
                },
            })

            return {
                ok: true,
                data: response.data,
            }
        } catch (error) {
            if (params.store !== false) dispatch({
                type: officeSalesSessionConstants.LIST_OFFICE_SALES_SESSIONS_FAILURE,
                payload: {
                    officeId: String(officeId || ''),
                    error: getError(error),
                },
            })

            return {
                ok: false,
                error: getError(error),
            }
        }
    }
}

export const createOfficeSalesSession = (payload) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_REQUEST })

            const requestPayload = {
                ...payload,
                billingRequestId: payload?.billingRequestId || (payload?.currentTab === 'calc3d' ? createBillingRequestId() : undefined),
            }
            const response = await axiosInstance.post('/office-sales-session', requestPayload)

            dispatch({
                type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_SUCCESS,
                payload: {
                    session: response.data.session,
                },
            })

            syncOfficeBillingFromResponse(dispatch, payload?.officeId, response.data)

            return {
                ok: true,
                data: response.data.session,
                response: response.data,
            }
        } catch (error) {
            const errorText = getError(error)
            const code = error?.response?.data?.code || ''
            dispatch({
                type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_FAILURE,
                payload: { error: errorText },
            })

            if (['OFFICE_SUBSCRIPTION_REQUIRED', 'SCENE_BILLING_LIMIT_EXCEEDED', 'OFFICE_SUBSCRIPTION_FUNDS_REQUIRED'].includes(code)) {
                swal('Нельзя создать 3D-сцену', errorText, 'warning')
            }

            return { ok: false, error: errorText, code }
        }
    }
}

export const getOfficeSalesSession = (officeId, sessionId) => {
    return async (dispatch) => {
        const requestKey = getOfficeSalesSessionReadKey(officeId, sessionId)

        if (!requestKey) {
            const error = 'Не удалось определить офис или сессию'

            dispatch({
                type: officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_FAILURE,
                payload: { error },
            })

            return {
                ok: false,
                error,
            }
        }

        const existingRequest = officeSalesSessionReadRequests.get(requestKey)

        if (existingRequest) {
            return existingRequest
        }

        const request = (async () => {
            try {
                dispatch({ type: officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_REQUEST })

                const [cleanOfficeId, cleanSessionId] = requestKey.split(':')
                const response = await axiosInstance.get(
                    `/office-sales-session/session/${cleanOfficeId}/${cleanSessionId}`
                )

                dispatch({
                    type: officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_SUCCESS,
                    payload: {
                        session: response.data.session,
                    },
                })

                return {
                    ok: true,
                    data: response.data.session,
                    response: response.data,
                }
            } catch (error) {
                dispatch({
                    type: officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_FAILURE,
                    payload: {
                        error: getError(error),
                    },
                })

                return {
                    ok: false,
                    error: getError(error),
                }
            } finally {
                if (officeSalesSessionReadRequests.get(requestKey) === request) {
                    officeSalesSessionReadRequests.delete(requestKey)
                }
            }
        })()

        officeSalesSessionReadRequests.set(requestKey, request)

        return request
    }
}


export const getPublicOfficeSalesSession = ({
    officeId,
    sessionId,
    token,
}) => {
    return async () => {
        try {
            const response = await axiosInstance.get(
                `/office-sales-session/public/${officeId}/${sessionId}`,
                {
                    // Токен уже передаётся в query. Дополнительный нестандартный
                    // header здесь не нужен: он вызывал CORS preflight и ломал
                    // публичный вход на localhost/LAN при старом allowHeaders.
                    params: { token },
                }
            )

            return {
                ok: true,
                data: response.data,
                response: response.data,
            }
        } catch (error) {
            return {
                ok: false,
                error: getError(error, 'Не удалось открыть совместную сессию'),
                code: String(error?.response?.data?.code || ''),
                status: Number(error?.response?.status || 0),
                response: error?.response?.data || null,
            }
        }
    }
}

export const inviteOfficeSalesSessionMembers = (
    officeId,
    sessionId,
    userIds = []
) => {
    return async () => {
        try {
            const response = await axiosInstance.post(
                `/office-sales-session/${officeId}/${sessionId}/staff-invites`,
                { userIds }
            )

            return {
                ok: true,
                data: response.data,
                response: response.data,
            }
        } catch (error) {
            return {
                ok: false,
                error: getError(error, 'Не удалось отправить приглашения сотрудникам'),
                response: error?.response?.data || null,
            }
        }
    }
}

export const updateOfficeSalesSession = (officeId, sessionId, patch, options = {}) => {
    return async (dispatch) => {
        const responseMode = String(options?.responseMode || '').trim()
        const sceneSaveSource = String(options?.sceneSaveSource || '').trim()
        const traceId = String(options?.traceId || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`)
        const silentStore = options?.silentStore === true
        const requestStartedAt = getSceneSaveNow()
        const requestBytes = getJsonByteLength({ patch })

        try {
            if (!silentStore) {
                dispatch({ type: officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_REQUEST })
            }

            if (sceneSaveSource) {
                logSceneSaveRequest('PATCH start', {
                    traceId,
                    sceneSaveSource,
                    officeId: String(officeId || ''),
                    sessionId: String(sessionId || '').trim().toUpperCase(),
                    requestBytes,
                    responseMode: responseMode || 'full',
                })
            }

            const response = await axiosInstance.patch(
                `/office-sales-session/${officeId}/${sessionId}`,
                { patch },
                {
                    params: {
                        ...(responseMode ? { responseMode } : {}),
                        ...(sceneSaveSource ? { saveSource: sceneSaveSource } : {}),
                        ...(traceId ? { saveTraceId: traceId } : {}),
                    },
                }
            )

            if (!silentStore && response.data?.session) {
                dispatch({
                    type: officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_SUCCESS,
                    payload: {
                        session: response.data.session,
                    },
                })
            }

            if (patch?.currentTab === 'calc3d' && response.data?.session?.billingCharge) {
                syncOfficeBillingFromResponse(dispatch, officeId, response.data)
            }

            if (sceneSaveSource) {
                logSceneSaveRequest('PATCH finish', {
                    traceId,
                    sceneSaveSource,
                    elapsedMs: Math.round(getSceneSaveNow() - requestStartedAt),
                    requestBytes,
                    responseBytes: getJsonByteLength(response.data),
                    status: Number(response.status || 0),
                    revision: Number(response.data?.saveAck?.revision || response.data?.session?.calc3dState?.revision || 0),
                })
            }

            return {
                ok: true,
                data: response.data?.session || null,
                saveAck: response.data?.saveAck || null,
                response: response.data,
            }
        } catch (error) {
            const responseData = error?.response?.data || null
            const responseCode = String(responseData?.code || '').trim().toUpperCase()
            const status = Number(error?.response?.status || 0)
            const message = getError(error)

            if (!silentStore) {
                dispatch({
                    type: officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_FAILURE,
                    payload: {
                        error: message,
                    },
                })
            }

            if (sceneSaveSource) {
                logSceneSaveRequest('PATCH error', {
                    traceId,
                    sceneSaveSource,
                    elapsedMs: Math.round(getSceneSaveNow() - requestStartedAt),
                    requestBytes,
                    status,
                    code: responseCode,
                    error: message,
                })
            }

            return {
                ok: false,
                error: message,
                code: responseCode,
                status,
                response: responseData,
                networkError: !error?.response,
            }
        }
    }
}



export const copyOfficeSalesSessionObjects = (officeId, sourceSessionId, payload = {}) => {
    return async (dispatch) => {
        try {
            const cleanSourceSessionId = String(sourceSessionId || '').trim().toUpperCase()
            const cleanTargetSessionId = String(payload?.targetSessionId || '').trim().toUpperCase()
            const requestPayload = cleanSourceSessionId && cleanTargetSessionId && cleanSourceSessionId === cleanTargetSessionId
                ? {
                    ...payload,
                    // Same-scene copy must start exactly on top of the source.
                    // The pasted copy is selected after hydrate and translate mode opens,
                    // so the user decides where it goes instead of receiving a hidden offset.
                    sameSceneOffset: [0, 0, 0],
                }
                : payload

            const response = await axiosInstance.post(
                `/office-sales-session/${officeId}/${sourceSessionId}/copy-objects`,
                requestPayload
            )

            return {
                ok: true,
                data: response.data?.targetSession || null,
                response: response.data,
            }
        } catch (error) {
            return {
                ok: false,
                error: getError(error, 'Ошибка копирования объектов между сценами'),
                response: error?.response?.data || null,
            }
        }
    }
}

export const closeOfficeSalesSession = (officeId, sessionId, payload = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeSalesSessionConstants.CLOSE_OFFICE_SALES_SESSION_REQUEST })

            const response = await axiosInstance.patch(`/office-sales-session/${officeId}/${sessionId}/close`, payload)

            dispatch({
                type: officeSalesSessionConstants.CLOSE_OFFICE_SALES_SESSION_SUCCESS,
                payload: {
                    session: response.data.session,
                },
            })

            return {
                ok: true,
                data: response.data.session,
                response: response.data,
            }
        } catch (error) {
            dispatch({
                type: officeSalesSessionConstants.CLOSE_OFFICE_SALES_SESSION_FAILURE,
                payload: {
                    error: getError(error),
                },
            })

            return {
                ok: false,
                error: getError(error),
            }
        }
    }
}

export const reopenOfficeSalesSession = (officeId, sessionId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeSalesSessionConstants.REOPEN_OFFICE_SALES_SESSION_REQUEST })

            const response = await axiosInstance.patch(`/office-sales-session/${officeId}/${sessionId}/reopen`)

            dispatch({
                type: officeSalesSessionConstants.REOPEN_OFFICE_SALES_SESSION_SUCCESS,
                payload: {
                    session: response.data.session,
                },
            })

            return {
                ok: true,
                data: response.data.session,
                response: response.data,
            }
        } catch (error) {
            dispatch({
                type: officeSalesSessionConstants.REOPEN_OFFICE_SALES_SESSION_FAILURE,
                payload: {
                    error: getError(error),
                },
            })

            return {
                ok: false,
                error: getError(error),
            }
        }
    }
}

export const getOfficeSalesSessionInvite = (officeId, sessionId) => {
    return async () => {
        try {
            const response = await axiosInstance.get(`/office-sales-session/${officeId}/${sessionId}/invite`)
            return {
                ok: true,
                data: response.data?.invite || null,
                session: response.data?.session || null,
                response: response.data,
            }
        } catch (error) {
            return {
                ok: false,
                error: getError(error, 'Не удалось получить настройки совместной ссылки'),
            }
        }
    }
}

export const createOfficeSalesSessionInvite = (officeId, sessionId, payload = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_INVITE_REQUEST })

            const response = await axiosInstance.post(`/office-sales-session/${officeId}/${sessionId}/invite`, payload)

            dispatch({
                type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_INVITE_SUCCESS,
                payload: {
                    session: response.data.session,
                    inviteToken: response.data.inviteToken,
                    inviteExpiresAt: response.data.inviteExpiresAt,
                },
            })

            return {
                ok: true,
                data: response.data.session,
                inviteToken: response.data.inviteToken,
                inviteExpiresAt: response.data.inviteExpiresAt,
                response: response.data,
            }
        } catch (error) {
            dispatch({
                type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_INVITE_FAILURE,
                payload: {
                    error: getError(error),
                },
            })

            return {
                ok: false,
                error: getError(error),
            }
        }
    }
}


export const revokeOfficeSalesSessionInvite = (officeId, sessionId) => {
    return async (dispatch) => {
        try {
            const response = await axiosInstance.delete(`/office-sales-session/${officeId}/${sessionId}/invite`)

            if (response.data?.session) {
                dispatch({
                    type: officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_SUCCESS,
                    payload: { session: response.data.session },
                })
            }

            return {
                ok: true,
                data: response.data?.invite || null,
                session: response.data?.session || null,
                response: response.data,
            }
        } catch (error) {
            return {
                ok: false,
                error: getError(error, 'Не удалось отозвать совместную ссылку'),
            }
        }
    }
}

export const updateOfficeSalesSessionCart = (officeId, sessionId, payload = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: SESSION_CART_UPDATE_REQUEST })

            const response = await axiosInstance.patch(`/office-sales-session/${officeId}/${sessionId}/cart`, payload)

            dispatch({
                type: SESSION_CART_UPDATE_SUCCESS,
                payload: {
                    session: response.data.session,
                    message: response.data.message,
                },
            })

            return {
                ok: true,
                data: response.data.session,
                response: response.data,
            }
        } catch (error) {
            dispatch({
                type: SESSION_CART_UPDATE_FAILURE,
                payload: {
                    error: getError(error, 'Ошибка обновления корзины сессии'),
                },
            })

            return {
                ok: false,
                error: getError(error, 'Ошибка обновления корзины сессии'),
            }
        }
    }
}

export const convertOfficeSalesSessionToOrder = (officeId, sessionId, payload = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: SESSION_CONVERT_ORDER_REQUEST })

            const response = await axiosInstance.post(`/office-sales-session/${officeId}/${sessionId}/convert-to-order`, payload)

            dispatch({
                type: SESSION_CONVERT_ORDER_SUCCESS,
                payload: {
                    session: response.data.session,
                    order: response.data.order,
                    message: response.data.message,
                    alreadyConverted: !!response.data.alreadyConverted,
                },
            })

            return {
                ok: true,
                data: response.data.session,
                order: response.data.order,
                response: response.data,
            }
        } catch (error) {
            dispatch({
                type: SESSION_CONVERT_ORDER_FAILURE,
                payload: {
                    error: getError(error, 'Ошибка создания заказа из сессии'),
                },
            })

            return {
                ok: false,
                error: getError(error, 'Ошибка создания заказа из сессии'),
                code: error?.response?.data?.code || '',
                orderId: error?.response?.data?.orderId || '',
                orderNumber: error?.response?.data?.orderNumber || '',
            }
        }
    }
}

export const setActiveOfficeSalesSession = (session) => {
    return {
        type: officeSalesSessionConstants.SET_ACTIVE_OFFICE_SALES_SESSION,
        payload: {
            session,
        },
    }
}

export const patchActiveOfficeSalesSession = (patch) => {
    return {
        type: officeSalesSessionConstants.PATCH_ACTIVE_OFFICE_SALES_SESSION,
        payload: {
            patch,
        },
    }
}

export const clearActiveOfficeSalesSession = () => {
    return {
        type: officeSalesSessionConstants.CLEAR_ACTIVE_OFFICE_SALES_SESSION,
    }
}



export const setOfficeSalesSessionTemplate = (officeId, sessionId, payload = {}) => async (dispatch) => {
    try {
        const response = await axiosInstance.patch(`/office-sales-session/${officeId}/${sessionId}/template`, payload)
        dispatch({
            type: officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_SUCCESS,
            payload: { session: response.data.session },
        })
        return { ok: true, data: response.data.session, response: response.data }
    } catch (error) {
        return { ok: false, error: getError(error, 'Ошибка изменения шаблона сцены') }
    }
}

export const createOfficeSalesSessionFromTemplate = (officeId, sessionId, payload = {}) => async (dispatch) => {
    try {
        dispatch({ type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_REQUEST })
        const response = await axiosInstance.post(`/office-sales-session/${officeId}/${sessionId}/create-from-template`, {
            ...payload,
            billingRequestId: payload?.billingRequestId || createBillingRequestId(),
        })
        dispatch({
            type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_SUCCESS,
            payload: { session: response.data.session },
        })
        syncOfficeBillingFromResponse(dispatch, officeId, response.data)
        return { ok: true, data: response.data.session, response: response.data }
    } catch (error) {
        const errorText = getError(error, 'Ошибка создания сцены из шаблона')
        const code = error?.response?.data?.code || ''
        dispatch({
            type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_FAILURE,
            payload: { error: errorText },
        })
        if (['OFFICE_SUBSCRIPTION_REQUIRED', 'SCENE_BILLING_LIMIT_EXCEEDED'].includes(code)) {
            swal('Нельзя создать сцену из шаблона', errorText, 'warning')
        }
        return { ok: false, error: errorText, code }
    }
}

export const duplicateOfficeSalesSession = (officeId, sessionId, payload = {}) => async (dispatch) => {
    try {
        dispatch({ type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_REQUEST })
        const response = await axiosInstance.post(`/office-sales-session/${officeId}/${sessionId}/duplicate`, {
            ...payload,
            billingRequestId: payload?.billingRequestId || createBillingRequestId(),
        })
        dispatch({
            type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_SUCCESS,
            payload: { session: response.data.session },
        })
        syncOfficeBillingFromResponse(dispatch, officeId, response.data)
        return { ok: true, data: response.data.session, response: response.data }
    } catch (error) {
        const errorText = getError(error, 'Не удалось скопировать сессию')
        const code = error?.response?.data?.code || ''
        dispatch({
            type: officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_FAILURE,
            payload: { error: errorText },
        })
        swal('Сессия не скопирована', errorText, 'warning')
        return { ok: false, error: errorText, code }
    }
}
